/**
 * 組織レベルスコア集約ロジック (gb-org-questionnaire-74)
 *
 * docs/mockups/個人回答_組織スコア集約ロジック設計.md の確定仕様を実装する。
 * 働きがい / つながり / 離職の兆し の3スコアを 0〜100 に集約し、
 * 総合評価・天気マーク・ABCDグレードを算出する。
 *
 * 注: 入力(個人スコア配列)は現時点ではダミー。実データ接続時は
 * AnalysisResult.scores (gb74) の big5/ona/retention から個人スコアを算出して渡す。
 */

export type Grade = 'A' | 'B' | 'C' | 'D';
export type Weather = '☀' | '⛅' | '🌧' | '⚡';

export interface ScoreResult {
  /** 0〜100 */
  score: number;
  grade: Grade;
  weather: Weather;
  /** 1.0〜5.0 の調整値 (デバッグ・部署別表示用) */
  adjusted: number;
}

// ===== 共通変換 =====

/** 1.0〜5.0 の調整値 → 0〜100 (線形) */
export function toScore100(adjusted: number): number {
  const v = ((adjusted - 1.0) / (5.0 - 1.0)) * 100;
  return Math.max(0, Math.min(100, Math.round(v)));
}

/** 0〜100 → グレード・天気 (閾値 80/60/40) */
export function toGrade(score100: number): { grade: Grade; weather: Weather } {
  if (score100 >= 80) return { grade: 'A', weather: '☀' };
  if (score100 >= 60) return { grade: 'B', weather: '⛅' };
  if (score100 >= 40) return { grade: 'C', weather: '🌧' };
  return { grade: 'D', weather: '⚡' };
}

function makeResult(adjusted: number): ScoreResult {
  const score = toScore100(adjusted);
  const { grade, weather } = toGrade(score);
  return { score, grade, weather, adjusted };
}

// ===== 統計ヘルパー =====

export function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

export function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/** 25パーセンタイル (線形補間) */
export function quartile1(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const pos = (sorted.length - 1) * 0.25;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

// ===== 1. 働きがいスコア (中央値 + 低スコア帯ペナルティ) =====

/**
 * @param individuals 各従業員の働きがい個人スコア (6ファセット平均、1.0〜5.0)
 */
export function aggregateWellbeing(individuals: number[]): ScoreResult {
  const med = median(individuals);
  const lowRate = individuals.filter((v) => v <= 2.5).length / Math.max(1, individuals.length);
  const adjusted = med * (1 - lowRate * 0.3);
  return makeResult(adjusted);
}

// ===== 2. つながりスコア (5変数の均等平均 + 最低変数ブレンド) =====

export interface ConnectionVars {
  /** チーム内の助け合い (三角接続) */
  help: number;
  /** 特定の人への頼りすぎ (V字依存、反転後・高い=良い) */
  vdep: number;
  /** 社長への集中 (反転後・高い=良い) */
  boss: number;
  /** 一人で抱えている仕事 (属人化、反転後・高い=良い) */
  silo: number;
  /** 部署間のつながり (橋渡し経路) */
  bridge: number;
}

/**
 * @param vars 5変数の組織中央値 (各 1.0〜5.0、逆転項目は反転済み)
 */
export function aggregateConnection(vars: ConnectionVars): ScoreResult & { perVar: Record<keyof ConnectionVars, ScoreResult> } {
  const list = [vars.help, vars.vdep, vars.boss, vars.silo, vars.bridge];
  const evenMean = mean(list);
  const minVar = Math.min(...list);
  const adjusted = evenMean * 0.7 + minVar * 0.3;
  const result = makeResult(adjusted);
  const perVar = {
    help: makeResult(vars.help),
    vdep: makeResult(vars.vdep),
    boss: makeResult(vars.boss),
    silo: makeResult(vars.silo),
    bridge: makeResult(vars.bridge),
  };
  return { ...result, perVar };
}

// ===== 3. 離職の兆しスコア (平均 + Q1重視) =====

/**
 * @param individuals 各従業員の離職の兆し個人スコア (R-01〜03平均、1.0〜5.0、高い=良い)
 */
export function aggregateRetention(individuals: number[]): ScoreResult {
  const avg = mean(individuals);
  const q1 = quartile1(individuals);
  const adjusted = avg * 0.6 + q1 * 0.4;
  return makeResult(adjusted);
}

// ===== 4. 組織の総合評価 (3スコア平均、天気=最低スコア方式) =====

export interface OrgOverall {
  score: number;
  grade: Grade;
  weather: Weather;
  /** 最低スコアのカテゴリ ('wellbeing'|'connection'|'retention') */
  weakest: 'wellbeing' | 'connection' | 'retention';
}

export function aggregateOverall(
  wellbeing: ScoreResult,
  connection: ScoreResult,
  retention: ScoreResult,
): OrgOverall {
  const score = Math.round((wellbeing.score + connection.score + retention.score) / 3);
  const grade = toGrade(score).grade;
  // 天気は3スコアのうち最も低いスコアの天気
  const entries: Array<{ key: OrgOverall['weakest']; r: ScoreResult }> = [
    { key: 'wellbeing', r: wellbeing },
    { key: 'connection', r: connection },
    { key: 'retention', r: retention },
  ];
  const weakest = entries.reduce((a, b) => (a.r.score <= b.r.score ? a : b));
  return { score, grade, weather: weakest.r.weather, weakest: weakest.key };
}
